import React, { useContext, useCallback } from 'react';
import PropTypes from 'prop-types';
import _ from 'lodash';
import { ViewModelForm } from '@xengage/gw-portals-viewmodel-react';
import { SUBMISSION_PATHS, JURISDICTIONS, PAConstants } from 'wmic-pe-portals-utils-js';
import WMICPACoverageUtil from 'wmic-pe-capability-gateway-common-pa-react/util/WMICPACoverageUtil';
import { WMICHeading } from 'wmic-pe-components-platform-react';
import { TranslatorContext } from '@jutro/legacy/locale';
import WMICDriverEndorsementsCoverageComponent from './WMICDriverEndorsementsCoverageComponent/WMICDriverEndorsementsCoverageComponent';
import metadata from './WMICDriverEndorsementsComponent.metadata.json5';
import messages from './WMICDriverEndorsementsComponent.messages';

function WMICDriverEndorsementsComponent(props) {
    const { id, jobVM, onClauseChange, readOnly } = props;
    const translator = useContext(TranslatorContext);

    const coverages = _.get(jobVM, `${PAConstants.PACoveragesPaths.OFFERINGS_PATH}.coverages.driverEndorsements_WMIC`);
    const baseState = _.get(jobVM, SUBMISSION_PATHS.BASE_STATE_VALUE);
    const rateAsOfDate = _.get(jobVM, SUBMISSION_PATHS.RATE_AS_OF_DATE_VALUE);

    const getCov28Heading = jurisdiction => {
        if ([JURISDICTIONS.ALBERTA, JURISDICTIONS.YUKON].includes(jurisdiction)) {
            return messages.reductionOfCoverage_AB_YT;
        }

        if (jurisdiction == JURISDICTIONS.ONTARIO) {
            return messages.reductionOfCoverage_ON;
        }

        return messages.reductionOfCoverage;
    };

    const getDriverEndorsementHeading = useCallback(
        covPublicId => {
            if (
                [
                    PAConstants.accidentWaiverCovPublicId,
                    PAConstants.accidentWaiverMtcCovPublicId,
                    PAConstants.minorConvictionWaiver39bPublicId,
                ].includes(covPublicId)
            ) {
                return _.find(coverages.value, { publicID: covPublicId })?.name;
            }

            let heading = '';

            switch (covPublicId) {
                case PAConstants.reducAmtForNamedCov_28_PublicId:
                    heading = getCov28Heading(baseState);
                    break;
                case PAConstants.article6ExcludedDriver_WMIC_PublicId:
                    heading = messages.article6;
                    break;
                case PAConstants.reducAmtForNamedCov_28A_PublicId:
                    heading = messages.excludedDriver;
                    break;
                default:
                    return null;
            }

            return translator(heading);
        },
        [baseState, translator, coverages]
    );

    const renderDriverEndorsements = () => {
        const policyDrivers = jobVM?.lobData?.personalAuto?.coverables?.drivers?.value;

        return coverages?.value
            ?.filter(
                endorsement =>
                    WMICPACoverageUtil.getEligibleDriversForEndorsement(endorsement, policyDrivers, baseState).length > 0
            )
            ?.map(endorsement => (
                <>
                    <WMICHeading
                        id={`${id}_heading`}
                        underlined
                        title={getDriverEndorsementHeading(endorsement.publicID)}
                        className="gw-mb-3"
                    />
                    <WMICDriverEndorsementsCoverageComponent
                        id={endorsement.coverageUniqueID}
                        driverEndorsement={endorsement}
                        offeredCoverages={coverages}
                        jobVM={jobVM}
                        baseState={baseState}
                        rateAsOfDate={rateAsOfDate}
                        onClauseChange={onClauseChange}
                        hideUnchecked={false}
                        readOnly={readOnly}
                    />
                </>
            ));
    };

    const overrideProps = {
        driverEndorsementsCoveragesContainer: {
            content: renderDriverEndorsements(),
        },
    };

    const resolvers = {
        resolveComponentMap: {
            WMICDriverEndorsementsCoverageComponent,
        },
    };

    return (
        <ViewModelForm
            uiProps={metadata.componentContent}
            overrideProps={overrideProps}
            componentMap={resolvers.resolveComponentMap}
        />
    );
}

WMICDriverEndorsementsComponent.propTypes = {
    id: PropTypes.string.isRequired,
    jobVM: PropTypes.shape({}).isRequired,
    onClauseChange: PropTypes.func.isRequired,
    readOnly: PropTypes.bool.isRequired,
};

export default WMICDriverEndorsementsComponent;
